import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, SectionCard, StatCard, StatusBadge } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Package, PlusCircle, HandHelping, Trash2, Ban, Pencil, Undo2, Boxes, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "Other", sku: "", description: "", unit: "pcs", unit_value: "", quantity_total: "" };

export default function Inventory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("items");
  const [items, setItems] = useState([]);
  const [loans, setLoans] = useState([]);
  const [cats, setCats] = useState([]);
  const [emps, setEmps] = useState([]);
  const [itemOpen, setItemOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loanOpen, setLoanOpen] = useState(false);
  const [loanFor, setLoanFor] = useState(null);
  const [loanForm, setLoanForm] = useState({ employee_id: "", quantity: "1", due_date: "", notes: "" });
  const [woOpen, setWoOpen] = useState(false);
  const [woFor, setWoFor] = useState(null);
  const [woForm, setWoForm] = useState({ quantity: "1", reason: "" });

  const load = useCallback(() => {
    api.get("/inventory").then((r) => setItems(r.data)).catch(() => {});
    api.get("/inventory/loans").then((r) => setLoans(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/inventory/meta").then((r) => setCats(r.data.categories)).catch(() => {});
    api.get("/employees").then((r) => setEmps(r.data)).catch(() => {});
  }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setItemOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it, quantity_total: String(it.quantity_total) }); setItemOpen(true); };

  const saveItem = async () => {
    try {
      const body = { ...form, quantity_total: parseFloat(form.quantity_total || 0), unit_value: parseFloat(form.unit_value || 0) };
      if (editing) await api.put(`/inventory/${editing.id}`, body);
      else await api.post("/inventory", body);
      toast.success(editing ? "Item updated" : "Item added");
      setItemOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const delItem = async (id) => {
    if (!window.confirm("Delete this item and its loan records?")) return;
    try { await api.delete(`/inventory/${id}`); toast.success("Item deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openLoan = (it) => { setLoanFor(it); setLoanForm({ employee_id: "", quantity: "1", due_date: "", notes: "" }); setLoanOpen(true); };
  const submitLoan = async () => {
    try {
      await api.post(`/inventory/${loanFor.id}/loan`, { ...loanForm, quantity: parseFloat(loanForm.quantity || 1) });
      toast.success("Item loaned"); setLoanOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const returnLoan = async (id) => {
    try { await api.post(`/inventory/loans/${id}/return`); toast.success("Marked returned"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openWo = (it) => { setWoFor(it); setWoForm({ quantity: "1", reason: "" }); setWoOpen(true); };
  const submitWo = async () => {
    try {
      await api.post(`/inventory/${woFor.id}/write-off`, { ...woForm, quantity: parseFloat(woForm.quantity || 1) });
      toast.success("Stock written off"); setWoOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const totalItems = items.length;
  const totalUnits = items.reduce((s, i) => s + (i.quantity_total || 0), 0);
  const onLoan = loans.filter((l) => l.status !== "Returned").reduce((s, l) => s + (l.quantity || 0), 0);
  const lowStock = items.filter((i) => i.status !== "In Stock").length;

  return (
    <div className="animate-fade-up">
      <PageTitle overline="HR · Inventory" title="Inventory">
        {isAdmin && <Button onClick={openAdd} data-testid="add-item-btn"><PlusCircle className="h-4 w-4 mr-1.5" /> Add Item</Button>}
      </PageTitle>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Package} label="Item Types" value={totalItems} />
        <StatCard icon={Boxes} label="Total Units" value={totalUnits} />
        <StatCard icon={HandHelping} label="On Loan" value={onLoan} accent="text-warning" />
        <StatCard icon={AlertTriangle} label="Low / Out of Stock" value={lowStock} accent="text-destructive" />
      </div>

      <div className="flex gap-1 rounded-md border border-border p-0.5 w-fit mb-4">
        <button onClick={() => setTab("items")} className={`px-3 py-1 text-sm rounded ${tab === "items" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="tab-items">Items</button>
        <button onClick={() => setTab("loans")} className={`px-3 py-1 text-sm rounded ${tab === "loans" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="tab-loans">Loans</button>
      </div>

      {tab === "items" && (
        <SectionCard title="Stock">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Item</TableHead><TableHead>Category</TableHead>
                <TableHead className="text-right">Total</TableHead><TableHead className="text-right">Available</TableHead>
                <TableHead>Status</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No inventory items yet.</TableCell></TableRow>}
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`item-row-${it.id}`}>
                    <TableCell className="font-medium">{it.name}{it.sku && <span className="text-xs text-muted-foreground ml-1">· {it.sku}</span>}</TableCell>
                    <TableCell className="text-sm">{it.category}</TableCell>
                    <TableCell className="text-right font-mono-num">{it.quantity_total} {it.unit}</TableCell>
                    <TableCell className="text-right font-mono-num">{it.quantity_available}</TableCell>
                    <TableCell><StatusBadge status={it.status} /></TableCell>
                    {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="outline" className="h-8 w-8" title="Loan to staff" disabled={it.quantity_available <= 0} onClick={() => openLoan(it)} data-testid={`loan-${it.id}`}><HandHelping className="h-4 w-4" /></Button>
                        <Button size="icon" variant="outline" className="h-8 w-8 text-warning" title="Write off" disabled={it.quantity_available <= 0} onClick={() => openWo(it)} data-testid={`writeoff-${it.id}`}><Ban className="h-4 w-4" /></Button>
                        <Button size="icon" variant="outline" className="h-8 w-8" title="Edit" onClick={() => openEdit(it)} data-testid={`edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" title="Delete" onClick={() => delItem(it.id)} data-testid={`delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      )}

      {tab === "loans" && (
        <SectionCard title="T-Loans to Staff">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Item</TableHead><TableHead>Staff</TableHead>
                <TableHead className="text-right">Qty</TableHead><TableHead>Loaned</TableHead>
                <TableHead>Due</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {loans.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No loans recorded.</TableCell></TableRow>}
                {loans.map((l) => (
                  <TableRow key={l.id} data-testid={`loan-row-${l.id}`}>
                    <TableCell className="font-medium">{l.item_name}</TableCell>
                    <TableCell>{l.employee_name}</TableCell>
                    <TableCell className="text-right font-mono-num">{l.quantity}</TableCell>
                    <TableCell className="text-sm">{fmtDate(l.loan_date)}</TableCell>
                    <TableCell className="text-sm">{l.due_date ? fmtDate(l.due_date) : "—"}</TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                    <TableCell className="text-right">
                      {l.status !== "Returned" ? (
                        isAdmin ? <Button size="sm" variant="outline" onClick={() => returnLoan(l.id)} data-testid={`return-${l.id}`}><Undo2 className="h-4 w-4 mr-1" /> Return</Button> : <span className="text-xs text-muted-foreground">On loan</span>
                      ) : <span className="text-xs text-muted-foreground">Returned {fmtDate(l.return_date)}</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      )}

      {/* Add/Edit item */}
      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent className="bg-popover max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Item" : "Add Item"}</DialogTitle><DialogDescription>Manage an inventory item and its stock level.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Name</Label><Input data-testid="item-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="item-category" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>SKU (optional)</Label><Input data-testid="item-sku" value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Total Quantity</Label><Input type="number" min="0" data-testid="item-qty" value={form.quantity_total} onChange={(e) => setForm({ ...form, quantity_total: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Unit</Label><Input data-testid="item-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1.5" placeholder="pcs" /></div>
            </div>
            <div><Label>Unit Value ($) <span className="text-xs text-muted-foreground font-normal">— used to value unreturned T-loans on settlement</span></Label><Input type="number" min="0" step="0.01" data-testid="item-value" value={form.unit_value} onChange={(e) => setForm({ ...form, unit_value: e.target.value })} className="mt-1.5" placeholder="0.00" /></div>
            <div><Label>Description</Label><Textarea data-testid="item-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button onClick={saveItem} disabled={!form.name} data-testid="save-item">{editing ? "Save" : "Add Item"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan */}
      <Dialog open={loanOpen} onOpenChange={setLoanOpen}>
        <DialogContent className="bg-popover max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Loan · {loanFor?.name}</DialogTitle><DialogDescription>Available: {loanFor?.quantity_available} {loanFor?.unit}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Staff Member</Label>
              <Select value={loanForm.employee_id} onValueChange={(v) => setLoanForm({ ...loanForm, employee_id: v })}>
                <SelectTrigger data-testid="loan-emp" className="mt-1.5"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent className="bg-popover max-h-64">{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quantity</Label><Input type="number" min="1" data-testid="loan-qty" value={loanForm.quantity} onChange={(e) => setLoanForm({ ...loanForm, quantity: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Due / Return Date</Label><Input type="date" data-testid="loan-due" value={loanForm.due_date} onChange={(e) => setLoanForm({ ...loanForm, due_date: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Notes</Label><Textarea data-testid="loan-notes" value={loanForm.notes} onChange={(e) => setLoanForm({ ...loanForm, notes: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button onClick={submitLoan} disabled={!loanForm.employee_id} data-testid="submit-loan">Loan Out</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Write-off */}
      <Dialog open={woOpen} onOpenChange={setWoOpen}>
        <DialogContent className="bg-popover max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Write Off · {woFor?.name}</DialogTitle><DialogDescription>Remove damaged/lost units from stock. Available: {woFor?.quantity_available}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Quantity</Label><Input type="number" min="1" data-testid="wo-qty" value={woForm.quantity} onChange={(e) => setWoForm({ ...woForm, quantity: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Reason</Label><Textarea data-testid="wo-reason" value={woForm.reason} onChange={(e) => setWoForm({ ...woForm, reason: e.target.value })} className="mt-1.5" placeholder="e.g. Damaged beyond repair" /></div>
          </div>
          <DialogFooter><Button variant="destructive" onClick={submitWo} data-testid="submit-wo">Write Off</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
